import { ref, toValue, watch, type MaybeRefOrGetter } from 'vue'

export interface ShareLinkState {
  uuid: string
  shareState: boolean
  shareLinkState: string
}

export interface ShareLinkRequests {
  share: (uuid: string) => Promise<{ share_link: string }>
  unshare: (uuid: string) => Promise<unknown>
  contentLabel: string
}

/**
 * A saved item's view-only link: its shared state, and sharing and unsharing it. An answer for an
 * item the panel has since moved off is dropped, so it can't show one item's link on another.
 */
export function useShareLink(state: MaybeRefOrGetter<ShareLinkState>, { share, unshare, contentLabel }: ShareLinkRequests) {
  const sharing = ref(false)
  const shared = ref(toValue(state).shareState)
  const shareLink = ref(toValue(state).shareLinkState)

  watch(() => toValue(state), ({ shareState, shareLinkState }) => {
    shared.value = shareState
    shareLink.value = shareLinkState
    sharing.value = false
  })

  const isStillShowing = (uuid: string) => toValue(state).uuid === uuid

  /** Resolves true once the link exists, so the caller can record it. */
  async function shareContent(): Promise<boolean> {
    const { uuid } = toValue(state)
    sharing.value = true
    try {
      const response = await share(uuid)
      if (!isStillShowing(uuid)) return false
      shared.value = true
      shareLink.value = response.share_link
      return true
    } catch (error) {
      console.error(`error sharing ${contentLabel}`, error)
      return false
    } finally {
      if (isStillShowing(uuid)) sharing.value = false
    }
  }

  async function unshareContent() {
    const { uuid } = toValue(state)
    try {
      await unshare(uuid)
      if (isStillShowing(uuid)) shared.value = false
    } catch (error) {
      console.error(`error unsharing ${contentLabel}`, error)
    }
  }

  return { sharing, shared, shareLink, shareContent, unshareContent }
}
